import Logo from "../../components/Logo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router";
import FormInput from "../../components/FormInput";
import Button from "../../components/Button";
import { motion } from "motion/react";
import { useAuth } from "../../hooks/useAuth";
import Redirect from "../../components/Redirect";
import FullLoading from "../../components/FullLoading";

const signInSchema = z.object({
  email: z.string().email("Adresse email invalide"),
  password: z.string().min(1, "Mot de passe requis"),
});

export type SignInFormData = z.infer<typeof signInSchema>;

export default function SignInPage() {
  const { signIn } = useAuth();
  const { isAuthenticated, isAuthLoading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
  });

  const onSubmit = async (data: SignInFormData) => {
    signIn(data);
  };

  if (isAuthLoading) {
    return <FullLoading />;
  }

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <div data-testid="signin-page" className="flex flex-col justify-center items-center h-screen gap-4 bg-background">
      <Logo />

      <motion.div
        className="w-full max-w-md bg-dark border border-border rounded-lg px-4 py-8 text-primary"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-2xl font-bold mb-6 text-center">Se connecter</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormInput
            id="email"
            label="Email"
            type="email"
            register={register}
            error={errors.email}
            dataTestId="signin-email"
          />

          <FormInput
            id="password"
            label="Mot de passe"
            type="password"
            register={register}
            error={errors.password}
            dataTestId="signin-password"
          />

          <Button variant="primary" className="w-full h-12 mt-8" type="submit" dataTestId="signin-button">
            Se connecter
          </Button>
          <div className="text-center text-tertiary text-sm">
            Pas de compte ?{" "}
            <span className="text-accent">
              <Link to="/signup">S'inscrire</Link>
            </span>
          </div>
          <div className="text-center text-accent text-xs">
            <Link to="/ask-reset-password">Mot de passe oublié ?</Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
